/* seedSampleClients50New.js */

import {
  collection,
  doc,
  writeBatch,
  Timestamp,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

// -------------------------
// Helpers
// -------------------------

function makeEmailFromName(name) {
  return (
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ".")
      .replace(/(^\.)|(\.$)/g, "") + "@example.com"
  );
}

function randFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomBool(p = 0.5) {
  return Math.random() < p;
}

function pickSome(arr, min = 1, max = 3) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const cappedMax = Math.max(min, Math.min(max, arr.length));
  const n = Math.floor(min + Math.random() * (cappedMax - min + 1));
  const copy = [...arr];
  const out = [];
  while (out.length < n && copy.length) {
    const idx = Math.floor(Math.random() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
}

function randomDateWithinMonthsBack(monthsBack = 24) {
  const now = new Date();
  const daysBack = Math.floor(Math.random() * (monthsBack * 30));
  const d = new Date(now);
  d.setDate(now.getDate() - daysBack);
  return Timestamp.fromDate(d);
}

function randomBirthDate(minAge = 25, maxAge = 70) {
  const now = new Date();
  const age = Math.floor(minAge + Math.random() * (maxAge - minAge + 1));
  const month = Math.floor(Math.random() * 12);
  const day = Math.floor(1 + Math.random() * 28);
  const d = new Date(now.getFullYear() - age, month, day);
  return Timestamp.fromDate(d);
}

function randomIncorporationDate(yearsBack = 15) {
  const now = new Date();
  const daysBack = Math.floor(Math.random() * (yearsBack * 365));
  const d = new Date(now);
  d.setDate(now.getDate() - daysBack);
  return Timestamp.fromDate(d);
}

function randomPhone(countryCode) {
  const cc = String(countryCode || "").toUpperCase();

  if (cc === "AE")
    return `+9715${Math.floor(10000000 + Math.random() * 89999999)}`;
  if (cc === "CH")
    return `+41${Math.floor(700000000 + Math.random() * 299999999)}`;
  if (cc === "FR")
    return `+33${Math.floor(600000000 + Math.random() * 399999999)}`;
  if (cc === "GB")
    return `+44${Math.floor(7000000000 + Math.random() * 2999999999)}`;
  if (cc === "US")
    return `+1${Math.floor(2000000000 + Math.random() * 7999999999)}`;
  if (cc === "SG")
    return `+65${Math.floor(80000000 + Math.random() * 19999999)}`;
  if (cc === "DE")
    return `+49${Math.floor(1500000000 + Math.random() * 7999999999)}`;
  if (cc === "IT")
    return `+39${Math.floor(300000000 + Math.random() * 699999999)}`;

  return `+${Math.floor(100000000000 + Math.random() * 899999999999)}`;
}

function makeRowId(prefix = "row") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function ensureStr(v) {
  return String(v ?? "").trim();
}

// -------------------------
// MUST MATCH KycSectionSowSof.jsx (values, not labels)
// -------------------------

const SOW_KEYS = [
  "salary",
  "self_employed",
  "business_owner",
  "sale_business",
  "sale_real_estate",
  "rental_income",
  "investment_returns",
  "inheritance",
  "gift",
  "loan",
  "other",
];

const SOF_KEYS = [
  "salary",
  "business_income",
  "own_bank_transfer",
  "sale_property",
  "sale_investments",
  "sale_business",
  "loan",
  "family_gift",
  "third_party_non_family",
  "cash_deposit",
  "crypto_proceeds",
  "other",
];

const NET_WORTH_RANGES = [
  "50k-100k", "100k-250k", "250k-500k", "500k-1m",
  "1m-2m", "2m-5m", "5m-10m", "10m-20m",
];

const ANNUAL_INCOME_RANGES = [
  "50k-100k", "100k-150k", "150k-250k", "250k-350k",
  "350k-500k", "500k-750k", "750k-1m", "1m-2m",
];

// -------------------------
// Ownership seeding (MATCHES KycSectionOwnership.jsx)
// -------------------------

function normalizeArrangementType(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  if (s === "foundation") return "Foundation";
  return "Trust";
}

function baseRolesForType(arrType) {
  const t = normalizeArrangementType(arrType);
  if (t === "Foundation") return ["Founder", "Council member", "Beneficiary"];
  return ["Settlor", "Trustee", "Beneficiary"];
}

function makeDefaultPartiesForType(arrType, names = []) {
  const roles = baseRolesForType(arrType);
  const out = roles.map((role, idx) => ({
    _rowId: makeRowId("laP"),
    role,
    name: String(names[idx] || "").trim(),
    nationality: "",
    domicile: "",
    autoCreated: true,
  }));

  const extra = names.slice(roles.length).filter((x) => String(x || "").trim());
  extra.forEach((nm) => {
    out.push({
      _rowId: makeRowId("laP"),
      role: "Beneficiary",
      name: String(nm || "").trim(),
      nationality: "",
      domicile: "",
      autoCreated: true,
    });
  });

  return out;
}

function makeArrangement({
  type = "Trust",
  name = "",
  source = "manual",
  sourceRefRowId = "",
  autoCreated = false,
  relatedShareholderName = "",
  parties = null,
} = {}) {
  const t = normalizeArrangementType(type);
  return {
    _rowId: makeRowId("la"),
    arrangementType: t,
    name: String(name || ""),
    source: String(source || "manual"),
    sourceRefRowId: String(sourceRefRowId || ""),
    relatedShareholderName: String(relatedShareholderName || ""),
    autoCreated: !!autoCreated,
    parties: Array.isArray(parties) ? parties : makeDefaultPartiesForType(t),
    _autoName: String(name || ""),
  };
}

function makeShareholderRow({
  holderType = "Individual",
  name = "",
  nationality = "",
  countryOfResidence = "",
  ownershipPercent = "",
  beneficialOwnerType = "Beneficial owner",
  treeNodeId = "",
  flags = {},
} = {}) {
  return {
    _rowId: makeRowId("sh"),
    treeNodeId: String(treeNodeId || ""),
    name: String(name || "").trim(),
    holderType: String(holderType || "").trim(),
    beneficialOwnerType: String(beneficialOwnerType || "Beneficial owner"),
    nationality: String(nationality || ""),
    countryOfResidence: String(countryOfResidence || ""),
    ownershipPercent: String(ownershipPercent ?? ""),
    shareholderEntityType: "",

    isPIC: !!flags.isPIC,
    isHoldingPassive: !!flags.isHoldingPassive,
    isHoldingActive: !!flags.isHoldingActive,
    isPicOrPassiveHolding: !!flags.isPicOrPassiveHolding,
  };
}

// ✅ EXACT dropdown values from ControllingPersonsSection.jsx
const BASIS_INDIRECT = "Indirect ownership";
const BASIS_AGREEMENT = "Control via agreement";

function makeUboRow({
  relatedShareholderRowId = "",
  relatedShareholderNodeId = "",
  name = "",
  nationality = "",
  countryOfResidence = "",
  basisOfControl = "",
  effectiveOwnershipPercent = "",
} = {}) {
  const eff = String(effectiveOwnershipPercent ?? "");
  const rid = ensureStr(relatedShareholderRowId);

  // default if not provided
  let basis = ensureStr(basisOfControl);
  if (!basis) basis = rid === "__other__" ? BASIS_AGREEMENT : BASIS_INDIRECT;

  return {
    _rowId: makeRowId("ubo"),
    relatedShareholderIdx: "",
    relatedShareholderNodeId: ensureStr(relatedShareholderNodeId),
    relatedShareholderRowId: rid,
    treeNodeId: "",
    name: ensureStr(name),
    nationality: ensureStr(nationality),
    countryOfResidence: ensureStr(countryOfResidence),

    basisOfControl: basis, // ✅ must match <option value="">
    effectiveOwnershipPercent: eff,
    ownershipPercent: eff,
    meetsThreshold: true,
  };
}

function makeControllerRow({
  name = "",
  role = "Director",
  nationality = "",
  countryOfResidence = "",
} = {}) {
  return {
    _rowId: makeRowId("sig"),
    name: ensureStr(name),
    role: ensureStr(role || "Director"),
    nationality: ensureStr(nationality),
    countryOfResidence: ensureStr(countryOfResidence),
    idNo: `ID-${Math.floor(100000 + Math.random() * 899999)}`,
  };
}

function makePersonName(firstNames, lastNames) {
  return `${randFrom(firstNames)} ${randFrom(lastNames)}`;
}

function splitPercents(total = 100, n = 2) {
  const cuts = [];
  for (let i = 0; i < n - 1; i++) cuts.push(Math.random());
  cuts.sort((a, b) => a - b);

  const parts = [];
  let prev = 0;
  for (const c of cuts) {
    parts.push(c - prev);
    prev = c;
  }
  parts.push(1 - prev);

  const raw = parts.map((p) => Math.max(1, Math.round(p * total)));

  let sum = raw.reduce((a, b) => a + b, 0);
  while (sum > total) {
    const idx = raw.findIndex((x) => x > 1);
    if (idx === -1) break;
    raw[idx] -= 1;
    sum -= 1;
  }
  while (sum < total) {
    const idx = Math.floor(Math.random() * raw.length);
    raw[idx] += 1;
    sum += 1;
  }
  return raw;
}

function buildOwnershipForEntity({
  entityName,
  country,
  firstNames,
  lastNames,
}) {
  const r = Math.random();
  const isQuoted = r < 0.1;
  const isAHTrust = !isQuoted && r >= 0.1 && r < 0.15;
  const isAHFoundation = !isQuoted && r >= 0.15 && r < 0.2;
  const isPIC = !isQuoted && !isAHTrust && !isAHFoundation && r < 0.3;

  const uboFlags = {
    pctNotMeaningful: !!isPIC,
    quotedCompany: !!isQuoted,
    accountHolderTrust: !!isAHTrust,
    accountHolderFoundation: !!isAHFoundation,
    hasOtherControl: false,
  };

  const controllers = [
    makeControllerRow({
      name: makePersonName(firstNames, lastNames),
      role: randFrom(["Director", "PoA", "Authorised signatory"]),
      nationality: randFrom(["AE", "CH", "FR", "GB", "US", "SG"]),
      countryOfResidence: country,
    }),
  ];
  if (randomBool(0.35)) {
    controllers.push(
      makeControllerRow({
        name: makePersonName(firstNames, lastNames),
        role: randFrom(["Director", "PoA", "Authorised signatory"]),
        nationality: randFrom(["AE", "CH", "FR", "GB", "US", "SG"]),
        countryOfResidence: country,
      })
    );
  }

  if (isQuoted) {
    return {
      uboFlags,
      shareholders: [],
      ubos: [],
      legalArrangements: { arrangements: [] },
      controllers,
    };
  }

  if (isAHTrust || isAHFoundation) {
    const t = isAHFoundation ? "Foundation" : "Trust";
    const partiesNames = [
      makePersonName(firstNames, lastNames),
      makePersonName(firstNames, lastNames),
      makePersonName(firstNames, lastNames),
    ];

    const arrangements = [
      makeArrangement({
        type: t,
        name: "",
        source: "account_holder",
        sourceRefRowId: "",
        autoCreated: true,
        relatedShareholderName: entityName,
        parties: makeDefaultPartiesForType(t, partiesNames),
      }),
    ];

    return {
      uboFlags,
      shareholders: [],
      ubos: [],
      legalArrangements: { arrangements },
      controllers,
    };
  }

  const shCount = randFrom([2, 3, 3, 4]);
  const percents = isPIC ? [] : splitPercents(100, shCount);

  const shareholders = [];
  const typesPool = [
    "Individual",
    "Individual",
    "Entity",
    "Entity",
    "Trust",
    "Foundation",
  ];

  for (let i = 0; i < shCount; i++) {
    const holderType = randFrom(typesPool);

    const name =
      holderType === "Individual"
        ? makePersonName(firstNames, lastNames)
        : holderType === "Entity"
        ? `${makePersonName(firstNames, lastNames)} Holding LLC`.replace(
            /\s+/g,
            " "
          )
        : holderType === "Trust"
        ? `${makePersonName(firstNames, lastNames)} Family Trust`
        : `${makePersonName(firstNames, lastNames)} Foundation`;

    const pct = isPIC ? "" : String(percents[i] ?? "");

    shareholders.push(
      makeShareholderRow({
        holderType,
        name,
        nationality: randFrom(["AE", "CH", "FR", "GB", "US", "SG"]),
        countryOfResidence: randFrom([country, "AE", "CH", "FR", "GB"]),
        ownershipPercent: pct,
        beneficialOwnerType: "Beneficial owner",
        flags: isPIC
          ? { isPIC: true, isHoldingPassive: true, isPicOrPassiveHolding: true }
          : {},
      })
    );
  }

  if (!isPIC) {
    const hasEntityShareholder = shareholders.some((s) => {
      const t = String(s.holderType || "").toLowerCase();
      return t === "entity";
    });

    if (!hasEntityShareholder) {
      shareholders[0].holderType = "Entity";
      shareholders[0].name = `${makePersonName(
        firstNames,
        lastNames
      )} Holding LLC`;
      shareholders[0].ownershipPercent = "60";
      if (shareholders[1]) shareholders[1].ownershipPercent = "40";
      for (let i = 2; i < shareholders.length; i++)
        shareholders[i].ownershipPercent = "0";
    }
  }

  // ✅ "Other control" => "__other__" UBO with basis "Control via agreement"
  uboFlags.hasOtherControl = randomBool(0.18);

  let ubos = [];

  if (uboFlags.hasOtherControl) {
    ubos.push(
      makeUboRow({
        relatedShareholderRowId: "__other__",
        name: makePersonName(firstNames, lastNames),
        nationality: randFrom(["AE", "CH", "FR", "GB", "US"]),
        countryOfResidence: country,
        basisOfControl: BASIS_AGREEMENT, // ✅ exact
        effectiveOwnershipPercent: "",
      })
    );
  }

  // Entity shareholders => demo natural person UBO with basis "Indirect ownership".
  // Real document eligibility is assessed by the ownership API, not this sample generator.
  if (!isPIC) {
    shareholders.forEach((sh) => {
      const t = String(sh.holderType || "").toLowerCase();
      const p = Number(String(sh.ownershipPercent || "").trim());
      if (t !== "entity") return;

      ubos.push(
        makeUboRow({
          relatedShareholderRowId: sh._rowId,
          name: makePersonName(firstNames, lastNames),
          nationality: randFrom(["AE", "CH", "FR", "GB", "US"]),
          countryOfResidence: randFrom([country, "AE", "CH", "FR"]),
          basisOfControl: BASIS_INDIRECT, // ✅ exact
          effectiveOwnershipPercent: String(p),
        })
      );

      if (randomBool(0.25)) {
        ubos.push(
          makeUboRow({
            relatedShareholderRowId: sh._rowId,
            name: makePersonName(firstNames, lastNames),
            nationality: randFrom(["AE", "CH", "FR", "GB", "US"]),
            countryOfResidence: randFrom([country, "AE", "CH", "FR"]),
            basisOfControl: BASIS_INDIRECT, // ✅ exact
            effectiveOwnershipPercent: String(Math.max(5, Math.round(p * 0.4))),
          })
        );
      }
    });
  }

  // ✅ HARD ENFORCE final values so dropdown always matches
  ubos = ubos.map((u) => {
    const rid = ensureStr(u?.relatedShareholderRowId);
    return {
      ...u,
      basisOfControl: rid === "__other__" ? BASIS_AGREEMENT : BASIS_INDIRECT,
    };
  });

  // Legal arrangements
  const arrangements = [];
  const tfEligible = (sh) => {
    const t = String(sh.holderType || "").toLowerCase();
    return t === "trust" || t === "foundation";
  };

  shareholders.forEach((sh) => {
    if (!tfEligible(sh)) return;

    const t = String(sh.holderType || "").trim();
    const partiesNames = [
      makePersonName(firstNames, lastNames),
      makePersonName(firstNames, lastNames),
      makePersonName(firstNames, lastNames),
    ];

    arrangements.push(
      makeArrangement({
        type: t,
        name: "",
        source: "shareholder",
        sourceRefRowId: sh._rowId,
        autoCreated: true,
        relatedShareholderName: sh.name,
        parties: makeDefaultPartiesForType(t, partiesNames),
      })
    );
  });

  return {
    uboFlags,
    shareholders,
    ubos,
    legalArrangements: { arrangements },
    controllers,
  };
}

// -------------------------
// Main seeder
// -------------------------

/**
 * @param {import("firebase/firestore").Firestore} db
 * @param {{ orgId: string, teamId?: string, teamName?: string, ownerUid?: string, createdByUid?: string, count?: number }} opts
 */
export async function seedSampleClients(db, opts = {}) {
  const orgId = String(opts.orgId || "").trim();
  const teamId = String(opts.teamId || "").trim();
  const teamName = String(opts.teamName || "").trim();
  const ownerUid = String(opts.ownerUid || "").trim();
  const createdByUid = String(opts.createdByUid || "").trim();
  const count = Number(opts.count || 50);
  const accessScope = teamId ? "team" : "individual";

  if (!orgId) {
    throw new Error(
      "seedSampleClients: orgId is required. Example: seedSampleClients(db, { orgId: myOrgId })"
    );
  }
  if (!teamId && !ownerUid) {
    throw new Error("seedSampleClients: provide either teamId or ownerUid.");
  }

  // ✅ bump tag whenever schema/seed behaviour changes
  const seedTag = `demo_clients_v17_access_scope_${count}`;

  const existingQ = teamId
    ? query(
        collection(db, "clients"),
        where("seedTag", "==", seedTag),
        where("orgId", "==", orgId),
        where("accessScope", "==", "team"),
        where("teamId", "==", teamId)
      )
    : query(
        collection(db, "clients"),
        where("seedTag", "==", seedTag),
        where("orgId", "==", orgId),
        where("accessScope", "==", "individual"),
        where("ownerUid", "==", ownerUid)
      );

  const existingSnap = await getDocs(existingQ);
  if (!existingSnap.empty) {
    return { inserted: 0, alreadySeeded: true, orgId, accessScope, teamId, ownerUid, seedTag };
  }

  const statuses = ["client", "prospect", "former"];

  const countries = [
    { iso2: "AE", city: "Dubai" },
    { iso2: "CH", city: "Geneva" },
    { iso2: "CH", city: "Zurich" },
    { iso2: "FR", city: "Paris" },
    { iso2: "GB", city: "London" },
    { iso2: "US", city: "San Francisco" },
    { iso2: "SG", city: "Singapore" },
    { iso2: "DE", city: "Frankfurt" },
    { iso2: "IT", city: "Milan" },
  ];

  const firstNames = [
    "Omar",
    "Sophie",
    "James",
    "Giulia",
    "Karim",
    "Aisha",
    "Noah",
    "Emily",
    "Wei",
    "Youssef",
    "Lucas",
    "Chloe",
    "Adam",
    "Lina",
    "Hugo",
    "Maya",
    "Nina",
    "Victor",
    "Samir",
    "Elena",
    "Rami",
    "Noura",
    "Julian",
    "Lea",
    "Matteo",
    "Sara",
    "Tom",
    "Fatima",
    "Nathan",
    "Aya",
  ];

  const lastNames = [
    "Al Mansoori",
    "Martin",
    "Harrington",
    "Rossi",
    "Bensaid",
    "Al Zahra",
    "Klein",
    "Clark",
    "Tan",
    "Haddad",
    "Morel",
    "Dubois",
    "Petrov",
    "Khan",
    "Nguyen",
    "Bernard",
    "Schmidt",
    "Bianchi",
    "Silva",
    "Fernandez",
    "Cohen",
    "Abdallah",
    "Ibrahim",
    "Steiner",
    "Ricci",
    "Lopez",
    "Mercier",
    "Kapoor",
    "Yilmaz",
    "Santos",
  ];

  const streetWords = [
    "Marina Gate",
    "Business Bay",
    "JLT Cluster",
    "Sheikh Zayed Road",
    "Rue du Rhône",
    "Avenue de Sécheron",
    "King’s Road",
    "Grosvenor Square",
    "Marina Boulevard",
    "Mainzer Landstraße",
    "Via della Moscova",
    "Rue de Turenne",
  ];

  const entityPrefixes = [
    "Blue",
    "Alpine",
    "Saffron",
    "Northbridge",
    "Riviera",
    "Helios",
    "Aurora",
    "Meridian",
    "Cedar",
    "Silverline",
    "Emerald",
    "Falcon",
    "Atlas",
    "Lighthouse",
    "Summit",
    "Golden",
    "Crescent",
    "Redwood",
    "Orion",
    "Harbor",
    "Zenith",
    "Nova",
    "Vertex",
    "Palm",
    "Crystal",
  ];

  const entityNouns = [
    "Holdings",
    "Capital",
    "Trading",
    "Logistics",
    "Hospitality",
    "Energy",
    "Medical",
    "Construction",
    "IT Services",
    "Advisory",
    "Investments",
    "Industries",
    "Partners",
    "Resources",
    "Ventures",
    "Group",
    "Management",
    "Real Estate",
    "Consulting",
    "Supply",
  ];

  const entitySuffix = [
    "LLC",
    "Ltd",
    "FZCO",
    "SARL",
    "SA",
    "GmbH",
    "Inc.",
    "Pte Ltd",
  ];

  const CLIENT_REQUEST_OPTIONS = [
    "Entity setup",
    "Bank account assistance",
    "Ongoing bookkeeping",
    "Tax/VAT support",
    "Corporate amendments",
    "Other",
  ];

  const APPROACH_CHANNEL_OPTIONS = ["Direct", "Referral", "Introducer"];

  const activityOptions = [
    "Consulting",
    "General Trading",
    "IT Services",
    "Real Estate Advisory",
    "Investment Holding",
    "Logistics",
    "Healthcare services",
    "Construction management",
  ];

  const samples = [];
  const half = Math.floor(count / 2);

  for (let i = 0; i < half; i++) {
    const c = randFrom(countries);
    const fullName = `${firstNames[i % firstNames.length]} ${
      lastNames[i % lastNames.length]
    }`;

    samples.push({
      clientName: fullName,
      clientType: "individual",
      status: randFrom(statuses),

      nationalityOrIncorporationCountry: c.iso2,
      address: `${Math.floor(5 + Math.random() * 95)} ${randFrom(streetWords)}`,
      city: c.city,
      country: c.iso2,

      contactName: null,
      contactEmail: "fabien2008@gmail.com",
      contactPhone: randomPhone(c.iso2),
    });
  }

  for (let i = 0; i < count - half; i++) {
    const c = randFrom(countries);
    const companyName = `${entityPrefixes[i % entityPrefixes.length]} ${
      entityNouns[i % entityNouns.length]
    } ${randFrom(entitySuffix)}`;

    samples.push({
      clientName: companyName,
      clientType: "entity",
      status: randFrom(statuses),

      nationalityOrIncorporationCountry: c.iso2,
      address: `Office ${Math.floor(100 + Math.random() * 1900)}, ${randFrom(
        streetWords
      )}`,
      city: c.city,
      country: c.iso2,

      contactName: randFrom([
        "Finance Director",
        "Operations Manager",
        "Compliance Officer",
        "General Counsel",
        "CFO Office",
        "Managing Partner",
      ]),
      contactEmail: "fabien2008@gmail.com",
      contactPhone: randomPhone(c.iso2),
    });
  }

  const batch = writeBatch(db);

  for (const s of samples) {
    const clientRef = doc(collection(db, "clients"));
    const clientId = clientRef.id;

    // CLIENT DOC
    batch.set(clientRef, {
      orgId,
      accessScope,
      ownerUid: accessScope === "individual" ? ownerUid : "",
      teamId,
      teamName,
      createdByUid: createdByUid || ownerUid || null,
      seedTag,
      clientName: s.clientName,
      clientType: s.clientType,
      status: s.status,

      address: s.address || "",
      city: s.city || "",
      country: s.country || "",

      contactName: s.contactName ?? null,
      contactEmail: s.contactEmail ?? null,
      contactPhone: s.contactPhone ?? null,

      joinDate: s.status === "prospect" ? null : randomDateWithinMonthsBack(28),
      leaveDate: null,

      kycCompleted: false,
      lastKycDate: null,

      kycFlowStage: "not_sent",
      kycInviteSentAt: null,
      kycLastSubmissionId: null,
      kycLastSubmissionAt: null,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const sowSelected = pickSome(SOW_KEYS, 1, 2);
    const sofSelected = pickSome(SOF_KEYS, 1, 3);

    const ownership =
      s.clientType === "entity"
        ? buildOwnershipForEntity({
            entityName: s.clientName,
	            country: s.country,
	            firstNames,
	            lastNames,
	          })
        : {
            uboFlags: {
              pctNotMeaningful: false,
              quotedCompany: false,
              accountHolderTrust: false,
              accountHolderFoundation: false,
              hasOtherControl: false,
            },
            shareholders: [],
            ubos: [],
            legalArrangements: { arrangements: [] },
            controllers: [],
          };

    // KYC DOC
    batch.set(
      doc(db, "kyc", clientId),
      {
        orgId,
        accessScope,
        ownerUid: accessScope === "individual" ? ownerUid : "",
        teamId,
        teamName,
        createdByUid: createdByUid || ownerUid || null,
        seedTag,
        clientId,
        clientStatus: String(s.status || "")
          .trim()
          .toLowerCase(),

        engagement: {
          clientRequests: pickSome(CLIENT_REQUEST_OPTIONS, 1, 3),
          approach: {
            channel: randFrom(APPROACH_CHANNEL_OPTIONS),
            introducerName: randomBool(0.35) ? "Relationship Manager" : "",
            paidIntroducer: randomBool(0.15),
          },
          noMeetingWithContractingPartner: randomBool(0.12),
        },

        customer: {
          type: s.clientType,
          name: s.clientName,
          nationalityOrIncorporationCountry: s.nationalityOrIncorporationCountry,

          dobOrIncorporationDate:
            s.clientType === "individual"
              ? randomBirthDate(25, 70)
              : randomIncorporationDate(18),

          email: "fabien2008@gmail.com",
          phone: s.contactPhone || "",
          address: s.address || "",
          city: s.city || "",
          country: s.country || "",
          website: s.clientType === "entity" ? "https://example.com" : "",
          businessActivity:
            s.clientType === "entity" ? randFrom(activityOptions) : "",
        },

        uaeCompany: {
          proposedEntityNames: "",
          jurisdiction: "Mainland",
          activities: "",
          expectedMonthlyTurnoverRange: "",
          expectedCustomerCountries: [],
          expectedSupplierCountries: [],
          expectedTransactionChannels: [],
          highRiskCountries: [],
        },

        ownership: {
          uboFlags: ownership.uboFlags || {},
          shareholders: ownership.shareholders || [],
          ubos: ownership.ubos || [],
          legalArrangements: ownership.legalArrangements || { arrangements: [] },
          controllers: ownership.controllers || [],
          complexStructure: randomBool(0.12),
          assistantTree: null,
        },

        sourceOfFundsWealth: {
          sourceOfFunds: randFrom([
            "Personal savings accumulated over time.",
            "Business revenues and retained earnings.",
            "Sale proceeds from assets / investments.",
            "Combination of salary and investment returns.",
          ]),
          sourceOfFundsDetails: "",

          sourceOfWealth: sowSelected,
          sourceOfWealthDetails: sowSelected.includes("other")
            ? "Other source (demo): family support / miscellaneous income."
            : "",

          netWorthCurrency: "USD",
          netWorthRange: randFrom(NET_WORTH_RANGES),
          annualIncomeCurrency: "USD",
          annualIncomeRange: randFrom(ANNUAL_INCOME_RANGES),

          sourceOfFundsTypes: sofSelected,

          thirdPartyDetails: sofSelected.includes("third_party_non_family")
            ? "Third party (demo): unrelated business partner transfer."
            : "",
          sourceOfFundsCashDetails: sofSelected.includes("cash_deposit")
            ? "Cash deposit (demo): explained as retail daily receipts."
            : "",
          sourceOfFundsOtherDetails: sofSelected.includes("other")
            ? "Other SoF (demo): mixed small sources."
            : "",
        },

        pepSanctions: {
          pepDeclared: randomBool(0.05),
          pepDetails: "",
          sanctionsDeclared: randomBool(0.03),
          sanctionsDetails: "",
        },

        sanctions: {
          clientDeclared: false,
          clientDetails: "",
          staffDecision: null,
          staffAssessment: "",
        },

        pep: {
          clientDeclared: false,
          clientDetails: "",
          staffDecision: null,
          staffAssessment: "",
        },

        adverseMedia: {
          negativeNews: null,
          language: "en",
          clientDeclared: false,
          clientDetails: "",
          staffDecision: null,
          staffAssessment: "",
        },

        documentsChecklist: { parties: {}, sow: {} },

        finalOverview: {
          calculatedRiskRating: "",
          eddRequired: null,
          sarStatus: "no",
          sarReference: "",
          relationshipAccepted: "",
          clientOnboarded: null,
          relationshipMaintained: null,
          negativeNews: false,
          profileSummary: "",
          assessment: "",
          complianceObservation: "",
        },

        internalUse: {
          industryRiskResult: null,
          geoRiskResult: null,
          geoRiskLevel: null,
          pepScreening: null,
          sanctionsScreening: null,
          adverseMediaScreening: null,
        },

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  await batch.commit();
  return { inserted: samples.length, alreadySeeded: false, orgId, accessScope, teamId, ownerUid, seedTag };
}
